import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import {
  EnterpriseSiteDTO,
  MrSiteSettingsForYardManagementInformationDTO,
  SiteDTO,
  SiteDTOPagedList,
  SiteFilterModel,
} from "src/app/api/api-types";
import { ApiUrl } from "src/app/api/api-url-helper";
import { SiteAvailabilityStatusEnum } from "src/app/core/constants";
import { RequestService } from "src/app/core/request.service";
import {
  deserializeSite,
  Site,
  SiteReference,
} from "src/app/core/sites/site.model";
import { Partner } from "src/app/partner/services/partners/partner.model";
import { PartnersService } from "src/app/partner/services/partners/partners.service";
import { SiteCreation } from "src/app/partner/sites/site-update.model";
import {
  firstExistentValueFrom,
  getDefaultUpdater,
  isExistent,
  isInstanceOf,
  isNot,
  mapNullable,
  pluckDistinctUntilChanged,
} from "src/utils";
import { UserService } from "../auth/user.service";
import { SiteStatus } from "./site-status.model";

@Injectable({ providedIn: "root" })
export class SitesService {
  public constructor(
    private readonly partners: PartnersService,
    private readonly request: RequestService,
    private readonly user: UserService,
  ) {}

  readonly #selectedId = new BehaviorSubject<Site["id"] | null>(null);
  public readonly selectedIdChanges = this.#selectedId.asObservable();

  readonly #listChanges = this.getListChanges().pipe(shareReplay(1));
  readonly #activeListChanges = this.#listChanges.pipe(
    // Filtering out of Pending/Decommissioned sites will be handled by the API
    // for most users, but we still need to filter out the Decommissioned sites
    // for global admins.
    mapNullable((sites) =>
      sites.filter(
        (site) => site.status !== SiteAvailabilityStatusEnum.Decommissioned,
      ),
    ),
    shareReplay(1),
  );

  public readonly maybeSelectedChanges = combineLatest([
    this.selectedIdChanges,
    this.#activeListChanges,
  ]).pipe(
    map(([selectedId, sites]) =>
      sites === null
        ? "loading"
        : sites.length === 0
        ? "list-empty"
        : sites.find((site) => site.id === selectedId) ?? "not-found",
    ),
  );

  public readonly selectedChanges = this.maybeSelectedChanges.pipe(
    filter(isInstanceOf(Site)),
    tap((site) => {
      this.user.updateUserPermissions(site.role);
    }),
  );

  public readonly selectedRoleChanges = this.selectedChanges.pipe(
    pluckDistinctUntilChanged("role"),
    shareReplay(1),
  );

  public readonly isSelectedLoadedChanges = this.maybeSelectedChanges.pipe(
    map((site) => site !== "loading" && site !== "not-found"),
  );

  private readonly defaultSelectionChanges = this.#activeListChanges.pipe(
    filter(isExistent),
    // The list could be empty before initial partner set up or if the user
    // isn't configured with all their correct sites yet.
    map((sites) => (sites[0] ?? null) as Site | null),
  );

  readonly #isSelectedLoadingChanges = this.isSelectedLoadedChanges.pipe(
    map(isNot(true)),
  );

  // When switching partners, the list of sites and the selected site both
  // reload, but the `selectedChanges` will retain the previous site until it
  // finishes loading, so there may be a moment when the the old selected site
  // is still loaded once the new list of sites appears. This means that the
  // selected site doesn't exist in the site list which shouldn't ever happen.
  //
  // To avoid this, we can hold back the emission of the newly updated list
  // until after the new selected site has been loaded/selected as well.
  public readonly listChanges = combineLatest([
    this.#listChanges,
    this.#isSelectedLoadingChanges,
  ]).pipe(
    map(([list, isSelectedLoading]) => (isSelectedLoading ? null : list)),
    distinctUntilChanged(),
  );
  public readonly activeListChanges = combineLatest([
    this.#activeListChanges,
    this.#isSelectedLoadingChanges,
  ]).pipe(
    map(([list, isSelectedLoading]) => (isSelectedLoading ? null : list)),
    distinctUntilChanged(),
  );

  private isSelectedLoadedNotifier?: Observable<boolean>;

  private readonly unloadNotifier = new Subject<void>();

  public getSettings: Observable<MrSiteSettingsForYardManagementInformationDTO> =
    this.selectedChanges.pipe(
      switchMap((site) =>
        this.request.get({
          prefix: site.getApiUrl(),
          path: ApiUrl.MR_SITE_SETTINGS,
        }),
      ),
    );

  public readonly statusListChanges: Observable<readonly SiteStatus[]> =
    this.request
      .get({
        path: ApiUrl.SITE_STATUSES,
      })
      .pipe(
        map((result) => SiteStatus.deserializeList(result)),
        shareReplay(1),
      );

  public getEnterpriseSites(
    partnerKey: Partner["key"],
  ): Observable<EnterpriseSiteDTO[]> {
    return this.request.get({
      path: ApiUrl.ENTERPRISE_SITES,
      pathParams: {
        partnerKey: partnerKey.toString(),
      },
    });
  }

  public getAllPaginatedListChanges(
    body: SiteFilterModel,
  ): Observable<Override<SiteDTOPagedList, { list: Site[] }>> {
    return this.partners.selectedChanges.pipe(
      switchMap((partner) =>
        this.request
          .post({
            path: ApiUrl.All_SITES,
            pathParams: {
              partnerKey: partner.key.toString(),
            },
            body,
          })
          .pipe(
            map((result) => {
              return {
                list: result.list.map((site) => {
                  const { sites, ...partnerData } = partner;
                  return deserializeSite(site, {
                    partner: new Partner(partnerData),
                  });
                }),
                count: result.count,
              };
            }),
          ),
      ),
    );
  }

  public async load(): Promise<boolean> {
    if (!this.isSelectedLoadedNotifier) {
      // Load the cache first so the default selection doesn't get applied on
      // initial load, and only runs on subsequent changes.
      await firstExistentValueFrom(this.#activeListChanges);

      const [updater, notifier] = getDefaultUpdater(
        this.maybeSelectedChanges,
        this.defaultSelectionChanges,
      );
      this.isSelectedLoadedNotifier = notifier;

      // Set the default site
      // if one isn't selected or the selected wasn't found.
      updater
        .pipe(
          switchMap((site) => this.setSelected(site)),
          takeUntil(this.unloadNotifier),
        )
        .subscribe();
    }

    return firstValueFrom(this.isSelectedLoadedNotifier);
  }

  public async setSelected(siteReference: SiteReference): Promise<void> {
    this.#selectedId.next(siteReference.id);
    await firstValueFrom(
      this.selectedChanges.pipe(filter((site) => site.id === siteReference.id)),
    );

    // Wait for the selected notifier so we know the updated list and default
    // selection have all settled before the caller proceeds.
    if (this.isSelectedLoadedNotifier) {
      await firstValueFrom(
        this.isSelectedLoadedNotifier.pipe(filter((isLoaded) => isLoaded)),
      );
    }
  }

  public getListChanges(): Observable<readonly Site[] | null> {
    return this.partners.selectedChanges.pipe(map((partner) => partner.sites));
  }

  public async create(
    body: SiteCreation,
    partnerKey: Partner["key"],
  ): Promise<SiteDTO> {
    const response = await firstValueFrom(
      this.request.post({
        path: ApiUrl.CREATE_SITE,
        pathParams: {
          partnerKey: partnerKey.toString(),
        },
        body: body.serialize(),
      }),
    );

    this.partners.notifyUpdates();

    return response;
  }
}
